import React, { useEffect, useState } from 'react';
import { ClipboardList, DollarSign, Activity, RefreshCw, TrendingUp } from 'lucide-react';
import { vendorAdminApi } from '../services/api';
import { useVendorContext } from './VendorAdminLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import PreferredDriverStatus from './PreferredDriverStatus';

const STATUS_COLORS = {
  placed: 'bg-slate-100 text-slate-700',
  confirmed: 'bg-blue-50 text-blue-700',
  preparing: 'bg-amber-50 text-amber-700',
  on_the_way: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-rose-50 text-rose-700',
};

const VendorDashboard = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setStats(await vendorAdminApi.stats(impersonating ? vendor_id : undefined)); }
    catch { /* */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [vendor_id]);

  const fmt = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{stats?.vendor?.name ? `${stats.vendor.name} · Dashboard` : 'Dashboard'}</h1>
          <p className="text-slate-500 mt-1">Today’s performance — auto-refreshes every 30s</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ClipboardList} label="Orders today" value={stats?.orders?.today ?? '—'} sub={`${stats?.orders?.total ?? 0} all-time`} accent="bg-blue-50 text-blue-700" />
        <StatCard icon={Activity} label="Active orders" value={stats?.orders?.active ?? '—'} accent="bg-amber-50 text-amber-700" />
        <StatCard icon={DollarSign} label="Revenue today" value={fmt(stats?.revenue?.today)} accent="bg-emerald-50 text-emerald-700" />
        <StatCard icon={DollarSign} label="Revenue 7d" value={fmt(stats?.revenue?.last_7d)} accent="bg-rose-50 text-rose-700" />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="bg-white border border-slate-200 rounded-2xl p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Top items (30d)</h2>
          </div>
          {(!stats || stats.top_items?.length === 0) ? (
            <div className="text-sm text-slate-500 py-6 text-center">No item sales yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {stats.top_items.map((it, idx) => (
                <div key={it.id} className="py-3 flex items-center gap-4">
                  <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center text-sm font-semibold">{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{it.name}</div>
                    <div className="text-xs text-slate-500">{it.qty} sold</div>
                  </div>
                  <div className="font-semibold text-slate-900">{fmt(it.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="space-y-6">
          <PreferredDriverStatus vendorId={vendor_id} impersonating={impersonating} />

          <section className="bg-white border border-slate-200 rounded-2xl p-5">
            <h2 className="font-semibold text-slate-900">Order status</h2>
            <div className="mt-3 space-y-2">
              {stats && Object.keys(stats.orders.status_breakdown || {}).length === 0 && (
                <div className="text-sm text-slate-500 py-4 text-center">No orders yet.</div>
              )}
              {stats && Object.entries(stats.orders.status_breakdown || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <Badge className={`${STATUS_COLORS[k] || 'bg-slate-100 text-slate-700'} border-0 hover:opacity-100 capitalize`}>{k.replace(/_/g, ' ')}</Badge>
                  <span className="font-semibold text-slate-900">{v}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
        {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
      </div>
      <div className={`h-10 w-10 rounded-xl ${accent} flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default VendorDashboard;
